import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, Inject, Input, LOCALE_ID, ViewEncapsulation } from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { MtnaI18nLanguageOption, MtnaLocaleIds } from '@mtna/core-i18n-angular';
import { MtnaLogger, MtnaThemeStorageService } from '@mtna/core-angular';
import {
  changeLocale,
  ClientLogoLink,
  ENVIRONMENT,
  Environment,
  isClientLogoLink,
  isClientLogoLinkDark,
  isClientLogoLinkLight,
  RdsApiService,
  RdsIconLink,
  RdsTextLink,
} from '@rds/shared-api';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

/**
 * Toolbar with logo and configurable links
 *
 * @author Will Davis <will.davis@mtna.us>
 */
@Component({
  selector: 'rds-c-app-toolbar',
  template: `
    <mat-toolbar class="mat-elevation-z4" role="banner">
      <ng-content></ng-content>
      <a
        *ngIf="clientLogo"
        class="rds-app-toolbar-client-logo"
        [attr.aria-label]="clientLogo.ariaLabel"
        [attr.href]="clientLogo.url"
        [attr.target]="clientLogo.target"
      >
        <img alt="" role="presentation" [attr.src]="clientLogo$ | async" />
      </a>
      <span fxFlex class="rds-c-app-toolbar__empty-space"></span>
      <mtna-dense-outline-language-select
        *ngIf="environment.languages && environment.languages.length"
        i18n-ariaLabel
        ariaLabel="Application Language"
        prefixIcon="language"
        [abbreviateDisplay]="isSmallScreen$ | async"
        [color]="i18nSelectColor$ | async"
        [options]="_options"
        [ngModel]="localeId"
        (ngModelChange)="handleLanguageChange($event)"
      ></mtna-dense-outline-language-select>
      <a
        *ngFor="let link of textLinks; let last = last"
        fxHide.lt-md
        mat-button
        [attr.href]="link.url"
        [attr.target]="link.target || '_blank'"
        [class.last-link]="last"
        >{{ link.text | uppercase }}</a
      >
      <mat-divider *ngIf="textLinks?.length && socialLinks?.length" fxHide.lt-md [vertical]="true"></mat-divider>
      <a
        *ngFor="let socialLink of socialLinks; let last = last"
        fxHide.lt-md
        mat-icon-button
        [attr.aria-label]="socialLink.text"
        [attr.href]="socialLink.url"
        [attr.target]="socialLink.target || '_blank'"
        [class.last-link]="last"
      >
        <mat-icon *ngIf="socialLink.icon; else textFallback" [svgIcon]="socialLink.icon"></mat-icon>
        <ng-template #textFallback>{{ socialLink.text }}</ng-template>
      </a>
      <button
        *ngIf="textLinks?.length || socialLinks?.length"
        fxHide.gt-sm
        mat-icon-button
        i18n-aria-label
        aria-label="Show toolbar controls"
        [matMenuTriggerFor]="menu"
      >
        <mat-icon>more_vert</mat-icon>
      </button>
    </mat-toolbar>
    <mat-menu #menu="matMenu" class="rds-c-app-toolbar__actions-menu">
      <a *ngFor="let link of textLinks" mat-menu-item [attr.href]="link.url" [attr.target]="link.target || '_blank'">{{
        link.text | uppercase
      }}</a>
      <mat-divider *ngIf="textLinks?.length && socialLinks?.length"></mat-divider>
      <div fxLayout="row" fxLayoutAlign="center center">
        <a
          *ngFor="let socialLink of socialLinks"
          mat-menu-item
          [attr.aria-label]="socialLink.text"
          [attr.href]="socialLink.url"
          [attr.target]="socialLink.target || '_blank'"
        >
          <mat-icon *ngIf="socialLink.icon; else textFallback" class="app-toolbar-social-icon" [svgIcon]="socialLink.icon"></mat-icon>
          <ng-template #textFallback>{{ socialLink.text }}</ng-template>
        </a>
      </div>
    </mat-menu>
  `,
  styleUrls: ['./app-toolbar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RdsAppToolbarComponent {
  /** Set the color of the i18n select based on whether the app is in a light or dark theme. Defaults to `{dark:'accent', light:'primary'}` */
  @Input() i18nSelectColor: { dark: ThemePalette; light: ThemePalette } = { dark: 'accent', light: 'primary' };

  /** Color for the i18n select based on whether the current theme is light or dark. */
  i18nSelectColor$: Observable<ThemePalette> = this.themeStorage.onThemeUpdate.pipe(
    startWith(this.themeStorage.getCurrentTheme()),
    map((theme) => (theme?.isDark ? this.i18nSelectColor.dark : this.i18nSelectColor.light))
  );

  /** Client Logo svg based on light or dark theme. */
  clientLogo$: Observable<string | undefined> = this.themeStorage.onThemeUpdate.pipe(
    startWith(this.themeStorage.getCurrentTheme()),
    map((theme) => {
      if (theme?.isDark && isClientLogoLinkDark(this.clientLogo)) {
        return this.clientLogo.logoPathDark;
      } else if (isClientLogoLinkLight(this.clientLogo)) {
        return this.clientLogo.logoPathLight;
      }
      return undefined;
    })
  );
  isSmallScreen$: Observable<boolean> = this.breakpointObserver
    .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium])
    .pipe(map((result) => result.matches));
  /** The locale specific client logo object. */
  clientLogo: ClientLogoLink | undefined;

  /** Text based links */
  textLinks: Array<RdsTextLink> | undefined = this.rdsApiService.getConfig('toolbarTextLinks');
  /** Icon based social media links */
  socialLinks: Array<RdsIconLink> | undefined = this.rdsApiService.getConfig('toolbarSocialLinks');

  /** Options for the dense outline select. */
  _options: MtnaI18nLanguageOption[] = this.environment.languages || [];

  constructor(
    protected breakpointObserver: BreakpointObserver,
    @Inject(LOCALE_ID) public localeId: string,
    private logger: MtnaLogger,
    @Inject(ENVIRONMENT) public environment: Environment,
    private rdsApiService: RdsApiService,
    private themeStorage: MtnaThemeStorageService
  ) {
    if (environment.clientLogo) {
      if (isClientLogoLink(environment.clientLogo)) {
        // Single language app
        this.clientLogo = environment.clientLogo;
      } else {
        // Multi-lingual app
        this.clientLogo = environment.clientLogo[localeId];
      }
    }
  }

  handleLanguageChange(localeId: typeof MtnaLocaleIds.type) {
    // If the environment languages specify a different url to use than just the localeId, then use that.
    // For example, statscan's "fr-CA" translation is mapped to just "fr" in the url
    const currentLocale = this.environment.languages?.find((lang) => lang.value === this.localeId)?.url ?? this.localeId;
    const newLocale = this.environment.languages?.find((lang) => lang.value === localeId)?.url ?? localeId;
    changeLocale(currentLocale, newLocale, this.logger, this.environment.production);
  }
}
